/**
 * Marketing-only analytics loader.
 *
 * Privacy-first: the app surface (every route under Gate, and Capacitor-native)
 * stays tracker-free per Sister Signal's privacy commitment. Only the
 * marketing site loads any analytics, and only when an env var is set.
 *
 * Supported providers (set ONE in /app/frontend/.env or .env.production):
 *
 *   REACT_APP_PLAUSIBLE_DOMAIN=sistersignal.org
 *     # Optional: REACT_APP_PLAUSIBLE_SCRIPT=https://plausible.io/js/script.js
 *
 *   REACT_APP_GA4_ID=G-XXXXXXX
 *
 * If neither is set, this module is a no-op — no scripts injected, no cookies,
 * no requests. Safe to ship with no analytics configured.
 *
 * Plausible is the recommended provider — it is cookie-less, GDPR/PECR-friendly,
 * lightweight (<1 kB), and doesn't sell data. Sign up at plausible.io, add
 * sistersignal.org as a site, then set REACT_APP_PLAUSIBLE_DOMAIN and rebuild.
 */

let loaded = false;

function injectScript({ src, attrs = {} }) {
  // Idempotent — don't double-load on hot reload or fast nav.
  if (document.querySelector(`script[src="${src}"]`)) return;
  const el = document.createElement("script");
  el.src = src;
  el.async = true;
  el.defer = true;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
}

function injectInline(code) {
  const el = document.createElement("script");
  el.text = code;
  document.head.appendChild(el);
}

/** Call once when entering the marketing surface. Safe to call repeatedly. */
export function loadMarketingAnalytics() {
  if (loaded) return;
  if (typeof window === "undefined") return;

  const plausibleDomain = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
  const plausibleScript =
    process.env.REACT_APP_PLAUSIBLE_SCRIPT || "https://plausible.io/js/script.js";
  const ga4Id = process.env.REACT_APP_GA4_ID;

  if (plausibleDomain) {
    injectScript({
      src: plausibleScript,
      attrs: { "data-domain": plausibleDomain },
    });
    loaded = true;
    return;
  }

  if (ga4Id) {
    injectScript({ src: `https://www.googletagmanager.com/gtag/js?id=${ga4Id}` });
    injectInline(
      `window.dataLayer = window.dataLayer || [];` +
      `function gtag(){dataLayer.push(arguments);}` +
      `gtag('js', new Date());` +
      `gtag('config', '${ga4Id}', { anonymize_ip: true });`
    );
    loaded = true;
  }
}

/**
 * Track a custom event from the marketing site only. No-op if no provider
 * is configured.
 */
export function trackMarketingEvent(name, props = {}) {
  if (!loaded || typeof window === "undefined") return;
  if (typeof window.plausible === "function") {
    try { window.plausible(name, { props }); } catch { /* ignore */ }
    return;
  }
  if (typeof window.gtag === "function") {
    try { window.gtag("event", name, props); } catch { /* ignore */ }
  }
}
